import React from 'react';
import { editorial } from '../../data/collections';
import Reveal from '../ui/Reveal';
import DotTrail from '../ui/DotTrail';
import Parallax from '../ui/Parallax';
import { RevealLine } from '../ui/TextReveal';

/**
 * A full-bleed campaign band on the dark ink field — the visual break between
 * "who we are" and "where we are". Worn-on-model shots only, so the jewellery
 * is seen at scale rather than on a prop.
 */
export function Editorial() {
  return (
    <section className="relative overflow-hidden bg-ink-950 py-20 lg:py-28">
      <div
        className="pointer-events-none absolute inset-x-0 -top-32 h-64 opacity-20 blur-3xl"
        style={{ background: 'radial-gradient(ellipse at center, #f49100 0%, transparent 70%)' }}
        aria-hidden="true"
      />

      <div className="shell relative">
        <div className="flex flex-col items-center gap-4 text-center">
          <Reveal variant="scale">
            <DotTrail count={5} tone="gold" />
          </Reveal>
          <Reveal variant="fade" delay={80}>
            <p className="eyebrow text-champagne-300">Worn, not staged</p>
          </Reveal>
          <h2 className="max-w-2xl font-display text-[2.1rem] leading-tight text-canvas sm:text-5xl">
            <RevealLine delay={160} duration={1000}>
              How the pieces actually <span className="foil italic">sit</span>
            </RevealLine>
          </h2>
        </div>
      </div>

      {/* Overflowing rail — scrollable by touch, dragged by scrollbar-free swipe. */}
      <div className="no-scrollbar mt-14 flex gap-4 overflow-x-auto px-[var(--shell-x)] pb-2 sm:gap-6">
        {editorial.map((shot, i) => (
          <Reveal
            key={shot.slug}
            variant="up"
            distance={34}
            duration={900}
            delay={i * 90}
            className="shrink-0"
            style={{ width: 'clamp(15rem, 34vw, 22rem)' }}
          >
            <figure className="group relative overflow-hidden rounded-sm">
              <Parallax speed={-18} scale={0.06}>
                <img
                  src={shot.src}
                  alt={shot.alt}
                  loading="lazy"
                  decoding="async"
                  width={1200}
                  height={1500}
                  className="aspect-[4/5] w-full object-cover transition-transform duration-[900ms] ease-silk group-hover:scale-105"
                />
              </Parallax>
              {/* Deeper scrim: several of these frames are light at the foot,
                  where the caption sits. */}
              <div className="pointer-events-none absolute inset-0 bg-gradient-to-t from-ink-950/85 via-ink-950/10 to-transparent" />
              <figcaption className="absolute bottom-4 left-5 right-5 flex items-center gap-2 text-[0.62rem] uppercase tracking-widest text-canvas">
                <span className="tabular-nums text-champagne-300">
                  {String(i + 1).padStart(2, '0')}
                </span>
                <span className="text-canvas/30">/</span>
                <span className="truncate">{shot.label}</span>
              </figcaption>
            </figure>
          </Reveal>
        ))}
      </div>
    </section>
  );
}

export default Editorial;
